import React, { useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { operations as lmsOperations } from 'store/lmsManager'
import {
  operations as licenseOperations,
  selectors as licenseSelectors,
} from 'store/licenseManager'
import { operations as loadingOperations } from 'store/loadingManager'
import {
  operations as userSessionOperations,
  selectors as userSessionSelectors,
} from 'store/userSessionManager'
import { FlaggedCourseManagerGetter as CourseManagerGetter } from 'layers/content/v2/Hocs/FlaggedCourseManagerGetter'
import { programs } from 'layers/content/v2/hooks/useCourseManager'
import { selectCurrentProgramClasses } from 'utils/lmsHelpers'
import {
  isK5Dashboard,
  isMiddleSchoolDashboard,
} from 'utils/routing/routeHelpers'
import { K12_CORE_CURRICULUM_SERVICE_FLAG } from 'utils/featureFlags'

import NavigationHelper from 'layers/navigation/navigationHelper'
import { GetRootContent } from 'layers/content/Hocs/RootContent'
import withApiError, {
  SHOW_CONTENT_ON_401,
  SHOW_ERROR_IN_MODAL,
  ERROR_TYPE_CONTENTFUL,
  ERROR_TYPE_ACCESS,
  ERROR_TYPE_LMS,
} from 'layers/errorHandling/apiError'
import { ROLES } from 'config/constants'

import {
  DASHBOARD_URL,
  LOADING_TEXT_DASHBOARD,
  PROGRAM_FIELD_TEST,
} from './constants'
import Dashboard from './component'
import { checkReadyToLoad, getAvailableProgramGroups } from './helpers'

const DashboardContainer = ({
  activeDigitalLicenses,
  addedPAKs,
  contentfulPrograms,
  courseManager,
  createFTPathInstance,
  createSelaPathInstance,
  fetchActiveDigitalLicensesForUser,
  fetchInstructorData,
  fetchInstructorDataV2,
  fetchSeedIds,
  fetchSetupLicensesForUser,
  getEntry,
  hasDwbEnabledSite,
  hasDwbOnlySite,
  instructorData,
  isFetchingActiveDigitalLicenses,
  isFetchingInstructorData,
  isFetchingInstructorDataV2,
  isFetchingSetupLicenses,
  isLicenseManagerSending,
  isLoadingApp,
  isRefetching,
  match,
  navigationEvent,
  pageTitle,
  programGroup = [],
  recentDashboardTab,
  seedIds = {},
  setAppIsLoadingState,
  setAppNotLoadingState,
  setRecentDashboardTab,
  trainingPaths = [],
  unassignedLicenses,
  userAccessClaims,
  userContext,
  userProfile,
}) => {
  const prevAddedPAKsLengthRef = useRef(addedPAKs?.length)

  const createSelaPath = useCallback(() => {
    const { hasSelaLeaderClaim, hasSelaStaffClaim } = userAccessClaims || {}
    const hasSelaRole = hasSelaLeaderClaim || hasSelaStaffClaim
    const hasSelaInContentful =
      Array.isArray(contentfulPrograms) &&
      contentfulPrograms.some(program => program.internalTitle === 'SELA')

    if (hasSelaRole && hasSelaInContentful) {
      const { Sela: selaPath } = instructorData || {}
      const totalSelaPaths = selaPath?.length || 0

      if (totalSelaPaths === 0) {
        createSelaPathInstance()
      }
    }
  }, [
    userAccessClaims,
    contentfulPrograms,
    instructorData,
    createSelaPathInstance,
  ])

  const createFTPath = useCallback(() => {
    const { hasFTLeaderClaim, hasFTStaffClaim } = userAccessClaims || {}

    const hasFTAccessClaim = hasFTLeaderClaim || hasFTStaffClaim
    const hasFTInContentful =
      Array.isArray(contentfulPrograms) &&
      contentfulPrograms.some(
        program => program.internalTitle === PROGRAM_FIELD_TEST,
      )

    if (hasFTAccessClaim && hasFTInContentful) {
      const { SelaFT: ftPath } = instructorData || {}
      const totalFTPaths = ftPath?.length || 0

      if (totalFTPaths === 0) {
        createFTPathInstance()
      }
    }
  }, [
    userAccessClaims,
    contentfulPrograms,
    instructorData,
    createFTPathInstance,
  ])

  const setDefaultTab = useCallback(
    programsList => {
      const { checkIfUserHasMSSiteWideLicenseAndAccess } = userSessionSelectors
      let defaultTab

      if (recentDashboardTab) {
        defaultTab = recentDashboardTab
      } else {
        const availablePrograms = getAvailableProgramGroups({
          hasDwbEnabledSite,
          programGroup: programsList,
        })
        const hasMiddleSchoolSiteWideLicense = checkIfUserHasMSSiteWideLicenseAndAccess(
          activeDigitalLicenses,
          userAccessClaims,
        )
        const programsAreAvailable = availablePrograms?.length > 0
        const defaultProgram =
          hasMiddleSchoolSiteWideLicense && programsAreAvailable
            ? availablePrograms.filter(
                program => program.internalTitle === 'ms tab',
              )[0]
            : availablePrograms[0] || {}

        defaultTab = defaultProgram?.route
        defaultTab && setRecentDashboardTab(defaultTab)
      }

      return defaultTab
    },
    [
      activeDigitalLicenses,
      hasDwbEnabledSite,
      recentDashboardTab,
      setRecentDashboardTab,
      userAccessClaims,
    ],
  )

  const ifDashboardPathAddProgramRoute = useCallback(() => {
    if (!Array.isArray(activeDigitalLicenses)) {
      return null
    }
    if (programGroup && match.path === DASHBOARD_URL) {
      const defaultTab = setDefaultTab(programGroup)

      if (defaultTab) {
        navigationEvent(`${DASHBOARD_URL}/${defaultTab}`, 'REPLACE')
      }
    } else {
      const currentTab = match?.params?.tabName
      const shouldDashboardTabUpdate = currentTab !== recentDashboardTab
      shouldDashboardTabUpdate && setRecentDashboardTab(currentTab)
    }
  }, [
    activeDigitalLicenses,
    match,
    navigationEvent,
    programGroup,
    recentDashboardTab,
    setDefaultTab,
    setRecentDashboardTab,
  ])

  const userHasUnassignedLicenses = useCallback(() => {
    const { roles } = userProfile || {}
    const hasLicenses =
      roles?.includes(ROLES.SETUP_ADMIN) &&
      unassignedLicenses &&
      unassignedLicenses.length > 0
    return hasLicenses
  }, [userProfile, unassignedLicenses])

  // Collect all props for checkReadyToLoad
  const allProps = {
    activeDigitalLicenses,
    addedPAKs,
    contentfulPrograms,
    courseManager,
    createFTPathInstance,
    createSelaPathInstance,
    fetchActiveDigitalLicensesForUser,
    fetchInstructorData,
    fetchInstructorDataV2,
    fetchSeedIds,
    fetchSetupLicensesForUser,
    getEntry,
    hasDwbEnabledSite,
    hasDwbOnlySite,
    instructorData,
    isFetchingActiveDigitalLicenses,
    isFetchingInstructorData,
    isFetchingInstructorDataV2,
    isFetchingSetupLicenses,
    isLicenseManagerSending,
    isLoadingApp,
    isRefetching,
    match,
    navigationEvent,
    pageTitle,
    programGroup,
    recentDashboardTab,
    seedIds,
    setAppIsLoadingState,
    setAppNotLoadingState,
    setRecentDashboardTab,
    trainingPaths,
    unassignedLicenses,
    userAccessClaims,
    userContext,
    userProfile,
  }

  // componentDidMount equivalent
  useEffect(() => {
    const { roles } = userProfile || {}
    const isUserSetupAdmin = roles?.includes(ROLES.SETUP_ADMIN)
    // TODO: once LMS V2 is fully released, remove this flag check/add K5:
    const readyToLoad = checkReadyToLoad({ props: allProps })

    if (readyToLoad) {
      setAppIsLoadingState(LOADING_TEXT_DASHBOARD)
    }

    if (isUserSetupAdmin && !isFetchingSetupLicenses && !unassignedLicenses) {
      fetchSetupLicensesForUser()
    }

    fetchActiveDigitalLicensesForUser()
    fetchInstructorData()
    fetchInstructorDataV2(userContext)

    if (!seedIds) {
      fetchSeedIds()
    }
  }, [])

  // componentDidUpdate equivalent for path creation and data changes
  useEffect(() => {
    if (
      instructorData &&
      instructorData?.Sela &&
      userAccessClaims &&
      contentfulPrograms &&
      !isFetchingInstructorData &&
      !isFetchingInstructorDataV2
    ) {
      createSelaPath()
      createFTPath()
    }
  }, [
    instructorData,
    userAccessClaims,
    contentfulPrograms,
    isFetchingInstructorData,
    isFetchingInstructorDataV2,
    createSelaPath,
    createFTPath,
  ])

  // componentDidUpdate equivalent for addedPAKs change
  useEffect(() => {
    if (addedPAKs?.length > prevAddedPAKsLengthRef.current) {
      getEntry?.()
    }
    prevAddedPAKsLengthRef.current = addedPAKs?.length
  }, [addedPAKs, getEntry])

  // componentDidUpdate equivalent for dashboard path routing
  useEffect(() => {
    ifDashboardPathAddProgramRoute()
  }, [ifDashboardPathAddProgramRoute])

  // shouldUpdateLoadingState equivalent
  useEffect(() => {
    const readyToLoad = checkReadyToLoad({ props: allProps })

    if (readyToLoad && !isLoadingApp) {
      setAppIsLoadingState(LOADING_TEXT_DASHBOARD)
    }
    if (!readyToLoad && isLoadingApp) {
      setAppNotLoadingState()
    }
  })

  // componentWillUnmount equivalent
  useEffect(() => {
    return () => {
      if (isLoadingApp) {
        setAppNotLoadingState()
      }
    }
  }, [])

  const hasUnassignedLicenses = userHasUnassignedLicenses()
  const { Trainings, Sela, SelaFT } = instructorData || {}

  const classesByProgram = selectCurrentProgramClasses({
    instructorData,
    courseManager,
    match,
    userContext,
  })
  const showDashboard =
    !checkReadyToLoad({ props: allProps }) &&
    Array.isArray(activeDigitalLicenses)

  return [
    showDashboard && (
      <Dashboard
        classes={classesByProgram}
        contentfulPrograms={contentfulPrograms}
        hasDwbEnabledSite={hasDwbEnabledSite}
        hasUnassignedLicenses={hasUnassignedLicenses}
        isContentfulRefetching={isRefetching}
        isLicenseManagerSending={isLicenseManagerSending}
        key="dashboard"
        lmsFTPath={SelaFT}
        lmsSelaPath={Sela}
        lmsTrainings={Trainings}
        match={match}
        navigationEvent={navigationEvent}
        pageTitle={pageTitle}
        programGroup={programGroup}
        seedIds={seedIds}
        trainingPaths={trainingPaths}
        unassignedLicenses={unassignedLicenses}
        userProfile={userProfile}
      />
    ),
  ]
}

DashboardContainer.propTypes = {
  activeDigitalLicenses: PropTypes.array,
  addedPAKs: PropTypes.array,
  contentfulPrograms: PropTypes.array,
  courseManager: PropTypes.object,
  createFTPathInstance: PropTypes.func,
  createSelaPathInstance: PropTypes.func.isRequired,
  fetchActiveDigitalLicensesForUser: PropTypes.func.isRequired,
  fetchInstructorData: PropTypes.func.isRequired,
  fetchInstructorDataV2: PropTypes.func.isRequired,
  fetchSeedIds: PropTypes.func.isRequired,
  fetchSetupLicensesForUser: PropTypes.func.isRequired,
  getEntry: PropTypes.func,
  hasDwbEnabledSite: PropTypes.bool,
  hasDwbOnlySite: PropTypes.bool,
  instructorData: PropTypes.object,
  isFetchingActiveDigitalLicenses: PropTypes.bool,
  isFetchingInstructorData: PropTypes.bool.isRequired,
  isFetchingInstructorDataV2: PropTypes.bool.isRequired,
  isFetchingSetupLicenses: PropTypes.bool,
  isLicenseManagerSending: PropTypes.bool,
  isLoadingApp: PropTypes.bool,
  isRefetching: PropTypes.bool,
  licenseError: PropTypes.shape({
    response: PropTypes.shape({
      data: PropTypes.string,
      status: PropTypes.number,
      statusText: PropTypes.string,
    }),
  }),
  lmsError: PropTypes.shape({
    response: PropTypes.shape({
      data: PropTypes.string,
      status: PropTypes.number,
      statusText: PropTypes.string,
    }),
  }),
  match: PropTypes.object.isRequired,
  navigationEvent: PropTypes.func,
  pageTitle: PropTypes.string,
  programGroup: PropTypes.array,
  recentDashboardTab: PropTypes.string,
  seedIds: PropTypes.object,
  setAppIsLoadingState: PropTypes.func,
  setAppNotLoadingState: PropTypes.func,
  setRecentDashboardTab: PropTypes.func,
  trainingPaths: PropTypes.array,
  unassignedLicenses: PropTypes.array,
  userAccessClaims: PropTypes.object,
  userContext: PropTypes.object,
  userProfile: PropTypes.object,
}

const mapStateToProps = state => {
  const {
    courseManager,
    lmsManager,
    licenseManager,
    userAccessManager,
    userContextManager,
    userProfileManager,
    userSessionManager,
    loadingManager,
  } = state
  return {
    activeDigitalLicenses: licenseSelectors.selectActiveDigitalLicenses(state),
    addedPAKs: licenseManager.addedPAKs,
    hasDwbEnabledSite: licenseSelectors.selectHasDwbEnabledSite(state),
    hasDwbOnlySite: licenseSelectors.selectHasDwbOnlyActiveSite(state),
    instructorData: lmsManager.instructorData,
    isFetchingActiveDigitalLicenses:
      licenseManager.isFetchingActiveDigitalLicenses,
    isFetchingInstructorData: lmsManager.isFetching,
    isFetchingInstructorDataV2: lmsManager.isFetchingV2,
    isFetchingSetupLicenses: licenseManager.isFetchingSetupLicenses,
    isLicenseManagerSending: licenseManager.isSending,
    isLoadingApp: loadingManager?.isLoading,
    recentDashboardTab: userSessionManager.programDashboard.recentDashboardTab,
    seedIds: lmsManager.seedIds,
    trainingPaths: lmsManager.trainingPaths,
    unassignedLicenses: licenseManager.unassignedLicenses,
    userAccessClaims: userAccessManager.claims,
    userContext: userContextManager,
    userProfile: userProfileManager.profile,
    courseManager: courseManager,
  }
}

const mapDispatchToProps = {
  createSelaPathInstance: lmsOperations.createSelaPathInstance,
  createFTPathInstance: lmsOperations.createFTPathInstance,
  fetchActiveDigitalLicensesForUser:
    licenseOperations.fetchActiveDigitalLicensesForUser,
  fetchInstructorData: lmsOperations.fetchInstructorData,
  fetchInstructorDataV2: lmsOperations.fetchInstructorDataV2,
  fetchSeedIds: lmsOperations.fetchSeedIds,
  fetchSetupLicensesForUser: licenseOperations.fetchSetupLicensesForUser,
  setAppIsLoadingState: loadingOperations.setIsLoadingState,
  setAppNotLoadingState: loadingOperations.setNotLoadingState,
  setRecentDashboardTab:
    userSessionOperations.programDashboardSetRecentDashboardTab,
}

export const mapper = entry => {
  const {
    displayTitle: pageTitle,
    products: contentfulPrograms,
    programGroup,
  } = entry || {}
  return { contentfulPrograms, pageTitle, programGroup }
}

const options = {
  include: 3,
  mapper,
  spread: true,
}

export default GetRootContent(
  withApiError(
    CourseManagerGetter(DashboardContainer, {
      flagSubscriptions: [K12_CORE_CURRICULUM_SERVICE_FLAG],
      programResolver: (_, props) => {
        // Standalone DWB dashboard uses static links only — not K5/MS course managers.
        if (!props?.userContext?.sites || props?.hasDwbOnlySite) {
          return programs.none
        }

        if (isMiddleSchoolDashboard()) {
          return programs.middleSchool
        } else if (isK5Dashboard()) {
          return programs.elementary
        } else {
          return programs.none
        }
      },
    }),
    [ERROR_TYPE_CONTENTFUL, ERROR_TYPE_LMS, ERROR_TYPE_ACCESS],
    [SHOW_CONTENT_ON_401, SHOW_ERROR_IN_MODAL],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
  connect(null, NavigationHelper.mapDispatchToProps),
)
